import { useStore } from "#src/stores/Store.tsx";
import colors from "#src/utils/colors.ts";
import tw from "#src/utils/tw.ts";
import {
  Placement,
  autoUpdate,
  flip,
  size,
  useFloating,
} from "@floating-ui/react-dom";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react";
import React, { ReactNode, useMemo } from "react";

interface Props {
  onChange?: (selction) => void;
  value?: ReactNode;
  children?: ReactNode;
  placeholder?: string;
  allowClear?: boolean;
  name?: string;
  disabled?: boolean;
  showSearch?: boolean;
  notFoundContent?: string;
  className?: string;
  placement?: Placement;
  strategy?: "fixed" | "absolute";
  disableAutoUpdate?: boolean;
}

export default function InputSelect(props: Props) {
  const { x, y, refs, strategy, update, placement } = useFloating({
    placement: props.placement || "bottom-start",
    strategy: props.strategy ? props.strategy : "fixed",
    whileElementsMounted: (ref, floating, update) => {
      if (props.disableAutoUpdate) {
        return;
      }

      return autoUpdate(ref, floating, update, {
        animationFrame: true,
      });
    },
    middleware: [
      flip({
        padding: 8,
      }),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            minWidth: `${rects.reference.width}px`,
          });
        },
      }),
    ],
  });

  function getOptions(children: React.ReactNode) {
    const options = [];
    const childrenArray = React.Children.toArray(children);

    for (let i = 0; i < childrenArray.length; i++) {
      const child = childrenArray[i] as React.ReactElement<any>;

      if (!child.props) {
        continue;
      }

      if ("value" in child.props) {
        options.push({
          value: child.props.value,
          label: Array.isArray(child.props.children)
            ? child.props.children.filter((c) => typeof c === "string").join("")
            : child.props.children,
        });
      }

      if (child.props.children && Array.isArray(child.props.children)) {
        options.push(...getOptions(child.props.children));
      }
    }

    return options;
  }

  function onInput(e) {
    const value = e.target.value;

    // console.log("value", value);

    props.onChange(value);
  }

  function clearValue() {
    if (props.onChange) {
      props.onChange("");
    }
  }

  const options = useMemo(() => getOptions(props.children), [props.children]);
  const selected = options.find((o) => o.value === props.value);

  let button = selected ? selected.label : props.placeholder;

  if (props.value && !button) {
    button = props.value;
  }

  function renderChildren({ open, disabled }) {
    return (
      <div className="relative" onClick={update}>
        <ListboxButton
          ref={refs.setReference}
          className={tw(
            "h-12",
            "border",
            "border-borderGray",
            "rounded-full",
            "border-b-3",
            "bg-white",
            "pl-4",
            "pr-0",
            "flex",
            "items-center",
            "w-full",
            !disabled && "cursor-pointer",
            "transition",
            "overflow-hidden",
            !disabled &&
              "active:border-b active:border-t-3 active:border-darkGray active:bg-borderGray",
            disabled && "opacity-50",
            props.className
          )}
        >
          <div
            className={tw(
              "flex-1",
              "text-left",
              "w-0",
              "truncate",
              !selected && "text-empty"
            )}
          >
            {button}
          </div>
          <div className="w-12 min-w-0">
            <i
              className={tw(
                "far fa-caret-down",
                "transform",
                "transition",
                "duration-200",
                "text-organization",
                open ? "rotate-180" : "rotate-0"
              )}
            />
          </div>
        </ListboxButton>

        {props.name && (
          <input
            name={props.name}
            onChange={onInput}
            className="fixed -bottom-96 opacity-0"
          />
        )}

        {props.allowClear && props.value && (
          <button
            className="absolute top-2.5 right-10 cursor-pointer"
            onClick={clearValue}
          >
            <i aria-hidden className="far fa-circle-xmark text-empty" />
          </button>
        )}

        <div
          ref={refs.setFloating}
          className={tw(
            "z-30",
            "max-h-60",
            "max-w-xs",
            !open && "pointer-events-none"
          )}
          style={{
            position: strategy,
            top: y ? (placement.includes("top") ? y - 8 : y + 8) : 0,
            left: x ?? 0,
          }}
        >
          <Transition
            show={open}
            className="border-borderGray z-10 w-full origin-top transform overflow-hidden rounded-sm border bg-white shadow-md transition data-closed:scale-95 data-closed:opacity-0"
            unmount={false}
            as="div"
          >
            <ListboxOptions
              // static
              className="m-0 max-h-60 w-full overflow-auto p-0"
            >
              {props.children}
            </ListboxOptions>
          </Transition>
        </div>
      </div>
    );
  }

  return (
    <Listbox
      as="span"
      value={props.value}
      onChange={props.onChange}
      disabled={props.disabled}
    >
      {renderChildren}
    </Listbox>
  );
}

export function InputOption(props) {
  const store = useStore();

  function renderChildren({ selected, active }) {
    let selectedColor = colors.action;

    if (!store.isAdminArea) {
      selectedColor = store.theme.orgColor;
    }

    return (
      <div
        className={tw(
          "block",
          "py-2",
          "px-4",
          "transition",
          "cursor-pointer",
          selected ? "text-white" : "hover:bg-borderGray",
          active && "bg-borderGray"
        )}
        style={{
          background: selected ? selectedColor : undefined,
        }}
      >
        {props.children}
      </div>
    );
  }

  return (
    <ListboxOption key={props.value} value={props.value}>
      {renderChildren}
    </ListboxOption>
  );
}
